import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiTrashCan, mdiPlus, mdiArrowLeft } from "@mdi/js";
import { toast } from "react-toastify";
import {
  ADD_PRODUCT_TITLE,
  PRODUCT_NAME_LABEL,
  PRODUCT_DESCRIPTION_LABEL,
  PRODUCT_IMAGE_LABEL,
  BENIFITS,
  DESCRIPTION_LABEL,
  PRODUCT,
  SERVICES,
  TYPE_LABEL,
  DETAILS_LABEL,
  EXPERTISE,
  AREA_LABEL,
  METHODOLOGY,
  STEPS_LABEL,
  CONTACT_LABEL,
  EMPTY_STRING,
  EDIT_PRODUCT,
  ADD_PRODUCT,
  BACK,
  UPDATE_LABEL,
  SUBMIT_LABEL,
  OVERVIEW_IMG,
  SERVICE_IMG,
  RS1_IMG,
  RS2_IMG,
  CONTACT_DETAILS_LABEL,
  IMAGE_ONE_MB_TOAST,
  ONLY_IMAGES_ALLOWED_TOAST,
  UPDATING_LABEL,
  SUBMITTING_LABEL,
} from "Components/constants/commonNames";
import {
  REQUEST_ADD_PRODUCT_COMMON,
  REQUEST_EDIT_PRODUCT_COMMON,
  REQUEST_FILE_UPLOAD_COMMON,
  REQUEST_VIEW_PRODUCT_COMMON,
} from "API/Services/common";
import BaseButton from "../../Components/Common/BaseButton";
import BaseInput from "../../Components/Common/BaseInput";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  InputPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import "../../STYLE/ProductsPage.css";
import { checkStatusCodeSuccess } from "Components/constants/enums";
import { BaseImageURL } from "API/Services/services";

const ProductsPage = () => {
  const navigate = useNavigate();
  const [fullScreenLoader, setFullScreenLoader] = useState(false);

  const { id } = useParams<{ id: string }>();
  const ProductId = id ? Number(id) : undefined;
  const [loader, setLoader] = useState<boolean>(false);
  const [benefits, setBenefits] = useState<string[]>([""]);
  const [services, setServices] = useState<
    { type: string; details: string[] }[]
  >([{ type: "", details: [""] }]);
  const [methodology, setMethodology] = useState<string[]>([""]);
  const [expertise, setExpertise] = useState<
    { area: string; description: string }[]
  >([{ area: "", description: "" }]);

  const [viewProductDate, setviewProductDate] = useState<any>();

  const validationSchema = Yup.object().shape({
    product_name: Yup.string().required(
      validationMessages.required(PRODUCT_NAME_LABEL)
    ),
    description: Yup.string().required(
      validationMessages.required(PRODUCT_DESCRIPTION_LABEL)
    ),

    overview_image: Yup.mixed().required(
      validationMessages.required(OVERVIEW_IMG)
    ),

    service_image: Yup.mixed().required(
      validationMessages.required(SERVICE_IMG)
    ),

    right_sidebar_image_1: Yup.mixed().required(
      validationMessages.required(RS1_IMG)
    ),

    right_sidebar_image_2: Yup.mixed().required(
      validationMessages.required(RS2_IMG)
    ),
    contact_us: Yup.string().required(
      validationMessages.required(CONTACT_LABEL)
    ),
  });

  const initialValues = {
    product_name: id ? String(viewProductDate?.product_name) : "",
    description: id ? String(viewProductDate?.description) : "",
    contact_us: id ? String(viewProductDate?.contact_us) : "",
    overview_image: id
      ? String(viewProductDate?.productImages?.[0]?.overview_image)
      : "",
    service_image: id
      ? String(viewProductDate?.productImages?.[0]?.service_image)
      : "",
    right_sidebar_image_1: id
      ? String(viewProductDate?.productImages?.[0]?.right_sidebar_image_1)
      : "",
    right_sidebar_image_2: id
      ? String(viewProductDate?.productImages?.[0]?.right_sidebar_image_2)
      : "",
    productBenefit: id
      ? viewProductDate?.productBenefit?.map((item: any) => {
          return item;
        })
      : [
          {
            product_benefit: "",
          },
        ],
    product_service: id
      ? viewProductDate?.productService?.map((detail: any) => ({
          type: detail.product_service_type,
          product_service_detail: detail.productServiceDetails.map(
            (item: any) => {
              return item.product_service_detail;
            }
          ),
        }))
      : [
          {
            type: "",
            product_service_detail: [""],
          },
        ],

    expertise_details: id
      ? viewProductDate?.productExpertise?.map((detail: any) => ({
          area: detail.expertise_area,
          description: detail.expertise_description,
        }))
      : [
          {
            area: "",
            description: "",
          },
        ],

    methodology_description: id
      ? viewProductDate?.productMethodology?.map((item: any) => {
          return item.methodology_description;
        })
      : [""],
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setLoader(true);
    const payload = {
      product_name: values.product_name,
      description: values.description,
      contact_us: values.contact_us,
      productImageDetail: [
        {
          overview_image: values.overview_image,
          service_image: values.service_image,
          right_sidebar_image_1: values.right_sidebar_image_1,
          right_sidebar_image_2: values.right_sidebar_image_2,
        },
      ],

      productBenefit: values.productBenefit.map((benefit: any) => ({
        product_benefit: benefit?.product_benefit,
      })),

      product_service: values.product_service?.map((service: any) => ({
        type: service.type,
        product_service_detail: service.product_service_detail,
      })),

      expertise_details: values.expertise_details.map((exp: any) => ({
        expertise_area: exp.area,
        expertise_description: exp.description,
      })),

      methodology_description: values.methodology_description,
    };

    try {
      const resp = ProductId
        ? await REQUEST_EDIT_PRODUCT_COMMON(ProductId, payload)
        : await REQUEST_ADD_PRODUCT_COMMON(payload);
      if (checkStatusCodeSuccess(resp.statusCode)) {
        toast.success(resp.message);
        navigate("/products");
      } else {
        toast.error(resp?.message);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setLoader(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const addBenefit = () =>
    formik.setFieldValue("productBenefit", [
      ...formik.values.productBenefit,
      "",
    ]);
  const removeBenefit = (index: number) => {
    if (index > 0) {
      const newBenefits = formik.values.productBenefit?.filter(
        (_: any, i: number) => i !== index
      );
      formik.setFieldValue("productBenefit", newBenefits);
    }
  };

  const addService = () => {
    formik.setFieldValue("product_service", [
      ...formik.values.product_service,
      { type: "", product_service_detail: [""] },
    ]);
  };

  const removeService = (index: any) => {
    if (index > 0) {
      const updatedServices = formik.values.product_service.filter(
        (_: any, i: any) => i !== index
      );
      formik.setFieldValue("product_service", updatedServices);
    }
  };

  const handleServiceChange = (
    index: number,
    field: "type" | "details",
    value: string | string[]
  ) => {
    const newServices = [...services];
    if (field === "type") {
      newServices[index].type = value as string;
    } else {
      newServices[index].details = value as string[];
    }
    setServices(newServices);
  };

  const addDetailToService = (serviceIndex: any) => {
    if (id) {
      formik.setFieldTouched("product_service", true);
    }
    const updatedServices = [...formik.values.product_service];
    updatedServices[serviceIndex].product_service_detail.push("");
    formik.setFieldValue("product_service", updatedServices);
  };

  const removeDetailFromService = (serviceIndex: any, detailIndex: any) => {
    const updatedServices = [...formik.values.product_service];
    updatedServices[serviceIndex].product_service_detail = updatedServices[
      serviceIndex
    ].product_service_detail.filter((_: any, i: any) => i !== detailIndex);
    formik.setFieldValue("product_service", updatedServices);
  };

  const handleDetailChange = (
    serviceIndex: number,
    detailIndex: number,
    value: string
  ) => {
    const newServices = [...services];
    newServices[serviceIndex].details[detailIndex] = value;
    setServices(newServices);
  };

  const addMethodology = () =>
    formik.setFieldValue("methodology_description", [
      ...formik.values.methodology_description,
      "",
    ]);
  const removeMethodology = (index: number) => {
    if (index > 0) {
      const newMethodology = formik.values.methodology_description?.filter(
        (_: any, i: number) => i !== index
      );
      formik.setFieldValue("methodology_description", newMethodology);
    }
  };

  const handleMethodologyChange = (index: number, value: string) => {
    const newMethodology = [...methodology];
    newMethodology[index] = value;
    setMethodology(newMethodology);
  };

  const addExpertise = () =>
    formik.setFieldValue("expertise_details", [
      ...formik.values.expertise_details,
      { area: "", description: "" },
    ]);

  const removeExpertise = (index: number) => {
    if (index > 0) {
      const newExpertise = formik.values.expertise_details.filter(
        (_: any, i: number) => i !== index
      );
      formik.setFieldValue("expertise_details", newExpertise);
    }
  };

  const handleExpertiseChange = (
    index: number,
    field: "area" | "description",
    value: string
  ) => {
    const newExpertise = [...formik.values.expertise_details];
    newExpertise[index][field] = value;
    formik.setFieldValue("expertise_details", newExpertise);
  };

  const [productImages, setProductImages] = useState({
    overview_image: "",
    service_image: "",
    right_sidebar_image_1: "",
    right_sidebar_image_2: "",
  });

  const handleFileChange = async (event: any, imageType: string) => {
    const fileInput = event.target;
    const file = fileInput?.files[0];
    const maxSizeInBytes = 1024 * 1024;

    if (file && file.size > maxSizeInBytes) {
      toast.error(IMAGE_ONE_MB_TOAST);
      fileInput.value = "";
      return;
    }

    const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (file && !allowedImageTypes.includes(file.type)) {
      toast.error(ONLY_IMAGES_ALLOWED_TOAST);
      fileInput.value = "";
      return;
    }

    if (file) {
      setFullScreenLoader(true);

      const formData = new FormData();
      formData.append("files", file);

      try {
        const resp: any = await REQUEST_FILE_UPLOAD_COMMON(formData);
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          formik.setFieldValue(imageType, String(resp?.data[0]?.file));
        } else {
          toast.error(resp.message);
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setFullScreenLoader(false);
      }
    }
  };

  const handleViewProduct = async (id: any) => {
    setFullScreenLoader(true);

    try {
      const resp: any = await REQUEST_VIEW_PRODUCT_COMMON(id);
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        setviewProductDate(resp?.data);
        toast.success(resp.message);
      } else {
        toast.error(resp.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setFullScreenLoader(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleViewProduct(id);
    }
  }, [id]);
  const isDetailsButtonDisabled = (service: any) => {
    if (!service.type || !service.type.trim()) {
      return true;
    }
    
    if (!service.product_service_detail || service.product_service_detail.length === 0) {
      return false;
    }
    
    const lastDetail = service.product_service_detail[service.product_service_detail.length - 1];
    return !lastDetail || !lastDetail.trim();
  };

  document.title = ADD_PRODUCT_TITLE;

  return (
    <div className="top-div">
      <div className="full-width-container">
        <Row className="align-items-center mt-2 mb-1">
          <Col md={6}>
            <span className="fs-4 mx-2">{id ? EDIT_PRODUCT : ADD_PRODUCT}</span>
          </Col>
          <Col md={6}>
            <BaseButton
              type="button"
              onClick={() => navigate("/products")}
              text={
                <>
                  <Icon path={mdiArrowLeft} size={0.7} className="me-2" />
                  {BACK}
                </>
              }
              color="info"
              className="mt-0 w-auto float-end"
            />
          </Col>
        </Row>

              <Card>
                <CardBody className="p-4">
                  <form
                    onSubmit={formik.handleSubmit}
                    className="d-flex w-100 flex-column"
                  >
                    <div className="border-bottom mb-2">
                      <Row>
                        <Col xs={12} sm={6} md={4} className="mb-3">
                          <BaseInput
                            required={true}
                            label={PRODUCT_NAME_LABEL}
                            name="product_name"
                            className="mb-3"
                            type="text"
                            placeholder={InputPlaceHolder(PRODUCT_NAME_LABEL)}
                            value={formik.values.product_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.errors.product_name}
                            touched={formik.touched.product_name}
                          />
                        </Col>
                        <Col xs={12} sm={6} md={4} className="mb-3">
                          <BaseInput
                            required={true}
                            label={PRODUCT_DESCRIPTION_LABEL}
                            name="description"
                            className="mb-3"
                            type="textarea"
                            placeholder={InputPlaceHolder(
                              PRODUCT_DESCRIPTION_LABEL
                            )}
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.errors.description}
                            touched={formik.touched.description}
                            rows={3}
                          />
                        </Col>
                        <Col xs={12} sm={6} md={4} className="mb-3">
                          <BaseInput
                            required={true}
                            label="Overview Image"
                            accept="image/*"
                            tooltip={true}
                            tooltipIcon="help-circle-outline"
                            tooltipIconColor="info"
                            tooltipText="Only JPEG/PNG Allowed"
                            name="overview_image"
                            type="file"
                            onChange={(event) => {
                              handleFileChange(event, "overview_image");
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.errors.overview_image}
                            touched={formik.touched.overview_image}
                          />
                          {formik.values.overview_image && (
                            <span className="fw-normal mb-2 d-flex">
                              <Link
                                target="_blank"
                                className="text-link font-weight-bold"
                                to={`${BaseImageURL}${formik.values.overview_image}`}
                              >
                                View File
                              </Link>
                            </span>
                          )}
                        </Col>
                        <Col xs={12} sm={6} md={4} className="mb-3">
                          <BaseInput
                            required={true}
                            label="Service Image"
                            accept="image/*"
                            tooltip={true}
                            tooltipIcon="help-circle-outline"
                            tooltipIconColor="info"
                            tooltipText="Only JPEG/PNG Allowed"
                            name="service_image"
                            type="file"
                            onChange={(event) => {
                              handleFileChange(event, "service_image");
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.errors.service_image}
                            touched={formik.touched.service_image}
                          />
                          {formik.values.service_image && (
                            <span className="fw-normal mb-2 d-flex">
                              <Link
                                target="_blank"
                                className="text-link font-weight-bold"
                                to={`${BaseImageURL}${formik.values.service_image}`}
                              >
                                View File
                              </Link>
                            </span>
                          )}
                        </Col>
                        <Col xs={12} sm={6} md={4} className="mb-3">
                          <BaseInput
                            required={true}
                            label="Right Sidebar Image 1"
                            name="right_sidebar_image_1"
                            tooltip={true}
                            tooltipIcon="help-circle-outline"
                            tooltipIconColor="info"
                            tooltipText="Only JPEG/PNG Allowed"
                            accept="image/*"
                            type="file"
                            onChange={(event) => {
                              handleFileChange(event, "right_sidebar_image_1");
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.errors.right_sidebar_image_1}
                            touched={formik.touched.right_sidebar_image_1}
                          />
                          {formik.values.right_sidebar_image_1 && (
                            <span className="fw-normal mb-2 d-flex">
                              <Link
                                target="_blank"
                                className="text-link font-weight-bold"
                                to={`${BaseImageURL}${formik.values.right_sidebar_image_1}`}
                              >
                                View File
                              </Link>
                            </span>
                          )}
                        </Col>
                        <Col xs={12} sm={6} md={4} className="mb-3">
                          <BaseInput
                            required={true}
                            label="Right Sidebar Image 2"
                            name="right_sidebar_image_2"
                            tooltip={true}
                            tooltipIcon="help-circle-outline"
                            tooltipIconColor="info"
                            tooltipText="Only JPEG/PNG Allowed"
                            accept="image/*"
                            type="file"
                            onChange={(event) => {
                              handleFileChange(event, "right_sidebar_image_2");
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.errors.right_sidebar_image_2}
                            touched={formik.touched.right_sidebar_image_2}
                          />
                          {formik.values.right_sidebar_image_2 && (
                            <span className="fw-normal mb-2 d-flex">
                              <Link
                                target="_blank"
                                className="text-link font-weight-bold"
                                to={`${BaseImageURL}${formik.values.right_sidebar_image_2}`}
                              >
                                View File
                              </Link>
                            </span>
                          )}
                        </Col>
                      </Row>
                    </div>

                    <div className="border-bottom mb-2 section-border">
                      <Row>
                        <Col xs={12} sm={6} md={4} className="border-right">
                          <div className="mb-2 d-flex justify-content-between align-items-center">
                            <span className="fs-3">{BENIFITS}</span>
                            <div className="addbtnsty">
                              <BaseButton
                                type="button"
                                onClick={addBenefit}
                                text={
                                  <Icon
                                    path={mdiPlus}
                                    className="fs-5"
                                    size={0.8}
                                  />
                                }
                                size="sm"
                                className="px-1"
                                color="info"
                                disabled={formik.values.productBenefit?.every((benefit: { product_benefit: any; }) => !benefit.product_benefit)}
                              />
                            </div>
                          </div>
                          {formik.values.productBenefit?.map(
                            (benefit: any, index: number) => (
                              <div key={index} className="mb-1">
                                <div className="d-flex justify-content-end">
                                  {index > 0 && (
                                    <div>
                                      <BaseButton
                                        type="button"
                                        onClick={() => removeBenefit(index)}
                                        text={
                                          <Icon path={mdiTrashCan} size={0.5} />
                                        }
                                        className="btn-danger btn-sm mt-0 "
                                      />
                                    </div>
                                  )}
                                </div>
                                <BaseInput
                                  label={DESCRIPTION_LABEL}
                                  className="mb-3"
                                  name={`productBenefit[${index}].product_benefit`}
                                  type="text"
                                  placeholder={InputPlaceHolder(
                                    DESCRIPTION_LABEL
                                  )}
                                  value={
                                    formik.values.productBenefit[index]
                                      .product_benefit
                                  }
                                  onChange={formik.handleChange}
                                />
                              </div>
                            )
                          )}
                        </Col>

                        <Col xs={12} sm={6} md={8}>
                          <div className="mb-2 d-flex justify-content-between align-items-center">
                            <span className="fs-3">{SERVICES}</span>
                            <div>
                              <BaseButton
                                type="button"
                                onClick={addService}
                                text={
                                  <Icon
                                    path={mdiPlus}
                                    className="fs-5"
                                    size={0.8}
                                  />
                                }
                                className="px-1"
                                size="sm"
                                color="info"
                                disabled={formik.values.product_service?.every((service: { type: any; }) => !service.type)}
                              />
                            </div>
                          </div>
                          {formik.values.product_service?.map(
                            (service: any, serviceIndex: any) => (
                              <div key={serviceIndex} className="mb-2">
                                <Row>
                                  <Col xs={12} md={6}>
                                    <BaseInput
                                      label={TYPE_LABEL}
                                      name={`product_service[${serviceIndex}].type`}
                                      className="mb-3"
                                      type="text"
                                      placeholder={InputPlaceHolder(TYPE_LABEL)}
                                      value={
                                        formik.values.product_service[
                                          serviceIndex
                                        ].type
                                      }
                                      onChange={formik.handleChange}
                                    />
                                  </Col>
                                  <Col xs={12} md={6}>
                                    <div className="d-flex justify-content-end">
                                      {serviceIndex > 0 && (
                                        <div>
                                          <BaseButton
                                            type="button"
                                            onClick={() =>
                                              removeService(serviceIndex)
                                            }
                                            text={
                                              <Icon
                                                path={mdiTrashCan}
                                                size={0.5}
                                              />
                                            }
                                            className="btn-danger btn-sm"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                </Row>

                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="fs-3">{DETAILS_LABEL}</span>
                                  <div>
                                    <BaseButton
                                      type="button"
                                      onClick={() =>
                                        addDetailToService(serviceIndex)
                                      }
                                      text={
                                        <Icon
                                          path={mdiPlus}
                                          className="fs-5"
                                          size={0.8}
                                        />
                                      }
                                      className="px-1"
                                      size="sm"
                                      color="info"
                                      disabled={isDetailsButtonDisabled(service)}
                                    />
                                  </div>
                                </div>

                                {service.product_service_detail?.map(
                                  (detail: any, detailIndex: any) => {
                                    return (
                                      <div
                                        key={detailIndex}
                                        className="mb-1 d-flex justify-content-between align-items-center border-bottom"
                                      >
                                        <Col xs={12} md={6} className="p-0">
                                          <BaseInput
                                            label={DETAILS_LABEL}
                                            name={`product_service[${serviceIndex}].product_service_detail[${detailIndex}]`}
                                            type="text"
                                            className="mb-3"
                                            placeholder={InputPlaceHolder(
                                              DETAILS_LABEL
                                            )}
                                            value={
                                              formik.values.product_service[
                                                serviceIndex
                                              ].product_service_detail[
                                                detailIndex
                                              ]
                                            }
                                            onChange={formik.handleChange}
                                          />
                                        </Col>
                                        {detailIndex > 0 && (
                                          <Col
                                            xs={12}
                                            md={6}
                                            className="p-0 d-flex"
                                          >
                                            <div>
                                              <BaseButton
                                                type="button"
                                                onClick={() =>
                                                  removeDetailFromService(
                                                    serviceIndex,
                                                    detailIndex
                                                  )
                                                }
                                                text={
                                                  <Icon
                                                    path={mdiTrashCan}
                                                    size={0.5}
                                                  />
                                                }
                                                className="btn-danger btn-sm "
                                              />
                                            </div>
                                          </Col>
                                        )}
                                      </div>
                                    );
                                  }
                                )}
                              </div>
                            )
                          )}
                        </Col>
                      </Row>
                    </div>

                    <div className="border-bottom mb-2">
                      <Row>
                        <Col xs={12} sm={6} md={6} lg={4} className="border-right">
                          <div className="mb-2 d-flex justify-content-between align-items-center">
                            <span className="fs-3">{METHODOLOGY}</span>
                            <div>
                              <BaseButton
                                type="button"
                                onClick={addMethodology}
                                text={
                                  <Icon
                                    path={mdiPlus}
                                    className="fs-5"
                                    size={0.8}
                                  />
                                }
                                className="px-1"
                                size="sm"
                                color="info"
                                disabled={formik.values.methodology_description?.every((method: any) => !method)}
                              />
                            </div>
                          </div>

                          {formik.values.methodology_description?.map(
                            (method: any, index: number) => (
                              <div key={index} className="mb-1">
                                <div className="d-flex justify-content-end">
                                  {index > 0 && (
                                    <div>
                                      <BaseButton
                                        type="button"
                                        onClick={() => removeMethodology(index)}
                                        text={
                                          <Icon path={mdiTrashCan} size={0.5} />
                                        }
                                        className="btn-danger btn-sm mt-0 "
                                      />
                                    </div>
                                  )}
                                </div>
                                <BaseInput
                                  label={STEPS_LABEL}
                                  name={`methodology_description[${index}]`}
                                  type="text"
                                  className="mb-3"
                                  placeholder={InputPlaceHolder(STEPS_LABEL)}
                                  value={
                                    formik.values.methodology_description[index]
                                  }
                                  onChange={formik.handleChange}
                                />
                              </div>
                            )
                          )}
                        </Col>

                        <Col  md={12} lg={8}>
                          <div className="mb-2 d-flex justify-content-between align-items-center">
                            <span className="fs-3">{EXPERTISE}</span>
                            <div>
                              <BaseButton
                                type="button"
                                onClick={addExpertise}
                                text={
                                  <Icon
                                    path={mdiPlus}
                                    className="fs-5"
                                    size={0.8}
                                  />
                                }
                                className="px-1"
                                size="sm"
                                color="info"
                                disabled={formik.values.expertise_details?.every((exp: { area: any; description: any; }) => !exp.area || !exp.description)}
                              />
                            </div>
                          </div>

                          {formik.values.expertise_details?.map(
                            (exp: any, index: number) => (
                              <div key={index}>
                                <div className="d-flex justify-content-end">
                                  {index > 0 && (
                                    <div>
                                      <BaseButton
                                        type="button"
                                        onClick={() => removeExpertise(index)}
                                        text={
                                          <Icon path={mdiTrashCan} size={0.5} />
                                        }
                                        className="btn-danger btn-sm mt-0"
                                      />
                                    </div>
                                  )}
                                </div>
                                <Row>
                                  <Col xs={12} sm={6} md={6} className="md-0">
                                    <BaseInput
                                      label={AREA_LABEL}
                                      name={`expertise_details[${index}].area`}
                                      type="text"
                                      placeholder={InputPlaceHolder(AREA_LABEL)}
                                      value={
                                        formik.values.expertise_details[index]
                                          .area
                                      }
                                      onChange={formik.handleChange}
                                      className="mb-3"
                                    />
                                  </Col>
                                  <Col xs={12} sm={6} md={6}>
                                    <BaseInput
                                      label={DESCRIPTION_LABEL}
                                      name={`expertise_details[${index}].description`}
                                      type="text"
                                      placeholder={InputPlaceHolder(
                                        DESCRIPTION_LABEL
                                      )}
                                      className="mb-3"
                                      value={
                                        formik.values.expertise_details[index]
                                          .description
                                      }
                                      onChange={(e) =>
                                        handleExpertiseChange(
                                          index,
                                          "description",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Col>
                                </Row>
                              </div>
                            )
                          )}
                        </Col>
                      </Row>
                    </div>
                    <div>
                      <Row>
                        <Col md={4}>
                          <span className="fs-3">{CONTACT_LABEL}</span>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12} sm={6} md={4}>
                          <BaseInput
                            required={true}
                            label={CONTACT_DETAILS_LABEL}
                            name="contact_us"
                            type="text"
                            placeholder={InputPlaceHolder(
                              `${CONTACT_LABEL} ${DETAILS_LABEL}`
                            )}
                            value={formik.values.contact_us}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.errors.contact_us}
                            touched={formik.touched.contact_us}
                          />
                        </Col>
                      </Row>
                    </div>

                    <div className="d-flex w-full justify-content-end">
                      <BaseButton
                        type="submit"
                        text={
                          id
                            ? loader
                              ? UPDATING_LABEL
                              : UPDATE_LABEL 
                            : loader
                            ? SUBMITTING_LABEL
                            : SUBMIT_LABEL
                        }
                        color="success"
                        className="mt-3 w-auto px-4 py-2"
                        size="sm"
                        loading={loader}
                      />
                    </div>
                  </form>
                </CardBody>
              </Card>
        {fullScreenLoader && (
          <div className="full-screen-loader">
            <Spinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
